import { useState, useEffect, useCallback } from 'react';
import { utils } from '@ohif/core';

const { apiService } = utils;

// 规范化数据索引的工具函数
const normalizeDataIndex = (data: any[], toLowerCase = true) => {
  const normalizedData = data.map(item => {
    const normalizedItem: { [key: string]: any } = {};
    Object.keys(item).forEach(key => {
      const normalizedKey = toLowerCase ? key.toLowerCase() : key.toUpperCase();
      normalizedItem[normalizedKey] = item[key];
    });
    return normalizedItem;
  });
  return normalizedData;
};

// 格式化日期的工具函数
const formatStudyDate = (studyTime: string) => {
  const date = new Date(studyTime);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return {
    yearMonthDay: `${year}-${month}-${day}`,
  };
};

// 转换状态的工具函数
const getStateText = (state: string) => {
  switch (state) {
    case '0':
      return '待检查';
    case '1':
      return '待诊断';
    case '2':
      return '已诊断';
    case '3':
    case '4':
    case '5':
      return '已审核';
    default:
      return '';
  }
};

// 定义 StudyData 类型
interface StudyData {
  register_key?: string;
  bodypart_name?: string;
  modality_code?: string;
  chinese_name?: string;
  sex?: string;
  age?: string;
  study_time?: string;
  check_view2?: string;
  diagnosis_result2?: string;
  yearMonthDay?: string;
  state?: string;
  stateText?: string;
  index?: number;
}

// 自定义 Hook：useStudyData
const useStudyData = ({ ...filterValues } = {},token?) => {
  const [studies, setStudies] = useState<StudyData[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [href, setHref] = useState<string>('');
  const [dataReady, setDataReady] = useState(false);
  const tokenManager = (window as any).tokenManager;

  const getData = useCallback(
    async (params: any = { ...filterValues }) => {
      const formData = new FormData();
      const paramKeys = [
        'study_id', // 检查号
        'patient_no', // 病人编号
        'patient_id', // 全院号
        'in_no', // 住院号
        'out_no', // 门诊号
        'identification_no', // 身份证号
        'ybkh', // 医保卡号
        'jzkh', // 就诊卡号
        'idCard', // 身份证号后六位
        'register_key',
      ];

      let paramsCount = 0;
      for (const key in params) {
        if (paramKeys.includes(key)) {
          formData.append(key, params[key]);
          paramsCount++;
        }
      }
      if(paramsCount === 0){
        return;
      }

      formData.append('cloud_image', '1'); //云影像特有标识
      setListLoading(true);
      try {
        const res = await apiService({
          uri: '/api/reglist/stream-page',
          method: 'POST',
          data: formData,
          type: null,
        });
        if (!res?.Data) {
          throw new Error('检查列表数据为空');
        }
        let data = res.Data;
        data = data.map((item: any, index: number) => ({
          ...item,
          index: index + 1,
        }));

        let normalizedData = normalizeDataIndex(data, true);
        normalizedData.sort((a, b) => {
          const parseDate = (dateString: string) => {
            if (!dateString) return -Infinity;
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? -Infinity : date.getTime();
          };
          return parseDate(b.study_time) - parseDate(a.study_time);
        });

        normalizedData = normalizedData.map((item: any) => ({
          ...item,
          yearMonthDay: item.study_time ? formatStudyDate(item.study_time).yearMonthDay : '',
          stateText: item.state ? getStateText(item.state) : '',
        }));

        setStudies(normalizedData);
        setDataReady(true);
      } catch (error) {
        setDataReady(false);
        throw new Error(`获取检查列表失败: ${error.message}`);
      } finally {
        setListLoading(false);
      }
    },
    [filterValues]
  );

  const getStudyInstanceUid = useCallback(async (register_key: string) => {
    const formData = new FormData();
    formData.append('register_key', register_key);
    try {
      const res = await apiService({
        uri: '/api/reglist/getSuid',
        method: 'POST',
        data: formData,
      });
      if (!res) {
        console.log('StudyInstanceUID 未返回');
      }
      return res;
    } catch (error) {
      throw new Error(`获取 StudyInstanceUID 失败: ${error.message}`);
    }
  }, []);

  // 初始化逻辑
  useEffect(() => {
    const url = window.location.href;
    setHref(window.location.origin);

    const params = new URLSearchParams(new URL(url).search);
    const filterValues: Record<string, string> = {};
    for (const [key, value] of params.entries()) {
      filterValues[key] = value;
    }

    const mergedParams = { ...filterValues };
    if(tokenManager.getCurrentToken()){
      getData(mergedParams);
    }

  }, [token]);

  return { studies, listLoading, href, getData, getStudyInstanceUid, dataReady };
};

export default useStudyData;
